import { keysToCamelCase } from './../../../shared/utils/utils';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import { all, call, put, SagaReturnType, select, takeLatest } from 'redux-saga/effects';
import {
    addDefectError,
    addDefectRequest,
    addDefectSuccess,
    closeDefectsError,
    closeDefectsRequest,
    closeDefectsSuccess,
    deleteDefectError,
    deleteDefectRequest,
    deleteDefectsError,
    deleteDefectsRequest,
    deleteDefectsSuccess,
    deleteDefectSuccess,
    editDefectError,
    editDefectRequest,
    editDefectSuccess,
    getDefectError,
    getDefectRequest,
    getDefectsError,
    getDefectsForSelectorError,
    getDefectsForSelectorRequest,
    getDefectsForSelectorSuccess,
    getDefectsRequest,
    getDefectsSuccess,
    getDefectSuccess,
    getJiraIssuesError,
    getJiraIssuesRequest,
    getJiraIssuesSuccess,
} from '.';
import {
    addDefect,
    closeDefects,
    deleteDefect,
    deleteDefects,
    editDefect,
    getDefect,
    getDefects,
    getJiraIssues,
} from '../api';
import { AxiosError, AxiosResponse } from 'axios';
import {
    Defect,
    DefectFormValues,
    DefectsMultipleActionParams,
    DefectsRequestParams,
    EditDefectParams,
    GetDefectParams,
    JiraIssuesRequestParams,
} from '../types';
import { selectDefect } from './selectors';

type GetDefectsResponse = SagaReturnType<typeof getDefects>;

export function* getDefectsSaga(action: PayloadAction<DefectsRequestParams>): SagaIterator {
    try {
        const response: GetDefectsResponse = yield call(getDefects, action.payload);
        yield put(getDefectsSuccess(response));
    } catch (error) {
        yield put(getDefectsError(error as AxiosError));
    }
}

type DeleteDefectsResponse = SagaReturnType<typeof deleteDefects>;

export function* deleteDefectsSaga(action: PayloadAction<DefectsMultipleActionParams>): SagaIterator {
    try {
        const response: DeleteDefectsResponse = yield call(deleteDefects, action.payload);
        yield put(deleteDefectsSuccess(response));
    } catch (error) {
        yield put(deleteDefectsError(error as AxiosError));
    }
}

type CloseDefectsResponse = SagaReturnType<typeof closeDefects>;

export function* closeDefectsSaga(action: PayloadAction<DefectsMultipleActionParams>): SagaIterator {
    try {
        const response: CloseDefectsResponse = yield call(closeDefects, action.payload);
        yield put(closeDefectsSuccess(response));
        const currentDefect: Defect | null = yield select(selectDefect);
        if (currentDefect && action.payload.ids.includes(currentDefect.pk)) {
            yield put(getDefectRequest({ defectId: currentDefect.pk }));
        }
    } catch (error) {
        yield put(closeDefectsError(error as AxiosError));
    }
}

export function* addDefectSaga(action: PayloadAction<DefectFormValues>): SagaIterator {
    try {
        const response: AxiosResponse<Defect> = yield call(addDefect, action.payload);
        yield put(addDefectSuccess(response.data));
    } catch (error) {
        yield put(addDefectError(error as AxiosError));
    }
}

type GetDefectResponse = SagaReturnType<typeof getDefect>;

export function* getDefectSaga(action: PayloadAction<GetDefectParams>): SagaIterator {
    try {
        const response: GetDefectResponse = yield call(getDefect, action.payload);
        yield put(getDefectSuccess(keysToCamelCase(response.data)));
    } catch (error) {
        yield put(getDefectError(error as AxiosError));
    }
}

type EditDefectResponse = SagaReturnType<typeof editDefect>;

export function* editDefectSaga(action: PayloadAction<EditDefectParams>): SagaIterator {
    try {
        const response: EditDefectResponse = yield call(editDefect, action.payload);
        yield put(editDefectSuccess({ pk: action.payload.pk, ...response.data }));
    } catch (error) {
        yield put(editDefectError(error as AxiosError));
    }
}

export function* deleteDefectSaga(action: PayloadAction<GetDefectParams>): SagaIterator {
    try {
        yield call(deleteDefect, action.payload);
        yield put(deleteDefectSuccess());
    } catch (error) {
        yield put(deleteDefectError(error as AxiosError));
    }
}

export function* getDefectsForSelectorSaga(action: PayloadAction<DefectsRequestParams>): SagaIterator {
    try {
        const response: GetDefectsResponse = yield call(getDefects, action.payload);
        yield put(getDefectsForSelectorSuccess(response));
    } catch (error) {
        yield put(getDefectsForSelectorError(error as AxiosError));
    }
}

export function* getJiraIssuesSaga(action: PayloadAction<JiraIssuesRequestParams>): SagaIterator {
    try {
        const response = yield call(getJiraIssues, action.payload);
        yield put(getJiraIssuesSuccess(response));
    } catch (error) {
        yield put(getJiraIssuesError(error as AxiosError));
    }
}

export default function* defectsSagaWatcher() {
    yield all([
        takeLatest(getDefectsRequest.type, getDefectsSaga),
        takeLatest(deleteDefectsRequest.type, deleteDefectsSaga),
        takeLatest(closeDefectsRequest.type, closeDefectsSaga),
        takeLatest(addDefectRequest.type, addDefectSaga),
        takeLatest(getDefectRequest.type, getDefectSaga),
        takeLatest(editDefectRequest.type, editDefectSaga),
        takeLatest(deleteDefectRequest.type, deleteDefectSaga),
        takeLatest(getDefectsForSelectorRequest.type, getDefectsForSelectorSaga),
        takeLatest(getJiraIssuesRequest.type, getJiraIssuesSaga),
    ]);
}
